import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { type CompanyRoute } from "@/lib/agent-agui";
import { isToolOffered, loadOfferedTools, type OfferedTool } from "@/lib/offered-tools";
import { useAgentStore } from "@/stores/agent-store";
import { ACCENT_PRESSED } from "./accent-pressed";

// A company question takes one of three paths on the agent-service: the expertise LABELS find
// companies nobody named, the recorded FIELDS answer a question about companies the question did
// name, and their own free TEXT answers what no field holds. Since 2026-10-15 the door no longer
// judges which — it extracts the names the question gives and whether the thing asked is a recorded
// field, and the path follows from that
// (@external-adr AGENT-SERVICE-0043-the-reader-can-overrule-the-company-door).
//
// Auto leaves that alone. The other three are instructions, and two of them are reachable ONLY from
// here: the door never derives "both halves" for anybody, and it sends a question naming nobody to
// the labels whatever it asks — so "descriptions only" over the whole population is a thing the
// reader can ask for and the door will not.
//
// A menu rather than the globe's toggle: four states, and `aria-pressed` says one of two things.
// Unlike the globe the choice HOLDS — diagnosing a route takes several questions in a row — but only
// in memory, so a reload clears it. A narrowing mode silently restored on Tuesday would shape every
// answer with a highlighted icon as its only notice.

// Fetched once per page load: the catalogue is compile-time data on the server and identical for
// every caller, so a second drawer mount has nothing new to learn.
let catalogue: Promise<OfferedTool[]> | null = null;
const offeredTools = () => (catalogue ??= loadOfferedTools());

interface RouteOption {
  route: CompanyRoute | null;
  id: string;
  label: string;
}

// A Record over the route type, not a hand-written list: a route added to `CompanyRoute` that the
// menu cannot offer is then a compile error rather than a mode the reader can never pick.
const NAMED: Record<CompanyRoute, { id: string; label: string }> = {
  description_and_label: { id: "agent.companyRoute.descriptionAndLabel", label: "Labels and descriptions" },
  description: { id: "agent.companyRoute.description", label: "Descriptions only" },
  label: { id: "agent.companyRoute.labelOnly", label: "Expertise labels only" },
};

const AUTO: RouteOption = { route: null, id: "agent.companyRoute.auto", label: "Automatic" };
const ROUTES: RouteOption[] = [
  AUTO,
  ...(Object.keys(NAMED) as CompanyRoute[]).map((route) => ({ route, ...NAMED[route] })),
];

// THE THRESHOLD, and what it does — measured, not illustrated.
//
// A threshold is a number with no meaning until you see what it admits, so the control shows one
// real word against real neighbours at their real similarities. Every score below was measured on
// 2026-10-14 against the live label vocabulary (1796 labels, mistral-embed), by scoring the word
// "Stromversorgung" against every other label and taking the nearest one in each band. Nothing here
// is invented: a made-up number in a calibration control teaches a fiction, and the reader would
// pick a threshold for a corpus that does not exist.
//
// What it teaches is the part a number cannot: at 0.75 "Telekommunikation" counts as
// "Stromversorgung", and at 0.70 so does "Trauerbegleitung".
const EXAMPLE_WORD = "Stromversorgung";
const EXAMPLE_LABELS: { label: string; score: number }[] = [
  { label: "Stromversorgung", score: 1.0 },
  { label: "Stromversorgungen", score: 0.982 },
  { label: "Stromrichter und Energieversorgungssysteme", score: 0.901 },
  { label: "Stromnetz- und Gasnetzbetrieb", score: 0.853 },
  { label: "Batterie-Wechselrichter", score: 0.801 },
  { label: "Telekommunikation", score: 0.75 },
  { label: "Trauerbegleitung", score: 0.7 },
  { label: "Ambulante Rehabilitation", score: 0.557 },
];

// The lowest similarity between ANY two labels in that vocabulary, measured the same day. It is why
// the first two ticks drop nothing: embeddings of German noun phrases simply do not go that low, so
// a threshold under it admits the entire vocabulary whatever the word.
const VOCABULARY_FLOOR = 0.49;

// Ten stops, dense where the answer actually changes. The even 0.1 steps the range input would give
// are mostly dead — see VOCABULARY_FLOOR — while every tick from 0.7 up drops one label of the
// example, which is what makes the control legible by dragging it.
//
// 0.88 IS HERE ON A MEASUREMENT rather than on that rule: on a corpus of German health providers
// (Lausitz, 2026-10-28) 0.80 pulled Kurzzeit-, Nacht- and vollstationäre Pflege into "Tagespflege"
// while 0.88 to 0.9 was exact. It takes 0.9's label with it — `Stromnetz- und Gasnetzbetrieb` at
// 0.853 — so 0.9 is now the one stop that changes nothing for THIS word. That is true of this
// word rather than of the control, and it is the better trade: a band real corpora need, against
// one stop that looks inert for one example.
const TICKS = [0.1, 0.5, 0.7, 0.75, 0.8, 0.85, 0.88, 0.9, 0.95, 1.0] as const;

// Where the slider rests before the reader has chosen anything: the agent-service's own default, so
// Automatic and the first drag are the same number rather than a jump to an end of the scale.
//
// THE SERVER'S NUMBER, not a nice one. This said 0.85 while the service used 0.80
// (`expertise.label-threshold` in agent-service's `application.yml`), so "Automatic" displayed a
// threshold it was not searching with — and the first drag silently LOOSENED the search while
// looking like it tightened it. Move this when that default moves.
const DEFAULT_TICK = 0.8;

/** The tick at [i], clamped — `noUncheckedIndexedAccess` makes the out-of-range case explicit. */
const tickAt = (i: number): number => TICKS[Math.min(Math.max(i, 0), TICKS.length - 1)] ?? DEFAULT_TICK;

const matches = (score: number, threshold: number) => score >= threshold;

/**
 * Where a score sits on the bar, as a percentage.
 *
 * Scaled from the measured FLOOR rather than from zero. On a 0–1 scale every bar would be between
 * 56% and 100% long and the ladder would read as eight near-identical stripes; the bottom half of
 * that scale is empty in the data anyway.
 */
const bar = (score: number) => Math.min(100, Math.max(0, ((score - VOCABULARY_FLOOR) / (1 - VOCABULARY_FLOOR)) * 100));

export function CompanySearchMode({
  section,
  id = "agent-company-route",
  disabled = false,
}: {
  section: string;
  id?: string;
  disabled?: boolean;
}) {
  const intl = useIntl();
  const route = useAgentStore((s) => s.companyRoute);
  const setCompanyRoute = useAgentStore((s) => s.setCompanyRoute);
  const threshold = useAgentStore((s) => s.labelThreshold);
  const setLabelThreshold = useAgentStore((s) => s.setLabelThreshold);
  const [tools, setTools] = useState<OfferedTool[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    // A catalogue that cannot be loaded hides the control rather than showing one that may be a lie
    // about where the search runs.
    offeredTools().then(
      (loaded) => alive && setTools(loaded),
      () => alive && setTools([]),
    );
    return () => {
      alive = false;
    };
  }, []);

  // It comes and goes with the section that offers the company search, rather than sitting dead on
  // screen where no question can reach it.
  if (!tools || !isToolOffered(tools, "FindCompanies", section)) return null;

  const current = ROUTES.find((option) => option.route === route) ?? AUTO;
  return (
    <div className="relative shrink-0">
      <Button
        id={id}
        variant="icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={intl.formatMessage({ id: "agent.companyRoute.label", defaultMessage: "Company search" })}
        title={intl.formatMessage(
          { id: "agent.companyRoute.title", defaultMessage: "Company search: {mode}" },
          { mode: intl.formatMessage({ id: current.id, defaultMessage: current.label }) },
        )}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className="shrink-0"
        style={route || threshold != null ? ACCENT_PRESSED : undefined}
      >
        <span aria-hidden="true" className="flex h-5 w-5 items-center justify-center font-semibold leading-none">
          C
        </span>
      </Button>
      {open && (
        <>
          <div aria-hidden onClick={() => setOpen(false)} className="fixed inset-0 z-[var(--z-dropdown)]" />
          {/* Upwards: the composer sits at the bottom of the drawer. */}
          <div role="menu" className="box bg-surface p-1 absolute bottom-full left-0 z-[var(--z-dropdown)] mb-1 w-80">
            {ROUTES.map((option) => (
              <MenuItem
                key={option.id}
                id={`${id}-${option.route ?? "auto"}`}
                role="menuitem"
                active={option.route === route}
                onClick={() => {
                  setCompanyRoute(option.route);
                  setOpen(false);
                }}
              >
                {intl.formatMessage({ id: option.id, defaultMessage: option.label })}
              </MenuItem>
            ))}
            {/* The menu does NOT close on a threshold change: the example below is the control, and a
                reader who cannot drag the slider and watch it is back to guessing at a number. */}
            <div className="border-subtle mt-1 border-t pt-1">
              <ThresholdSlider
                id={id}
                threshold={threshold}
                onChange={setLabelThreshold}
                setAuto={() => setLabelThreshold(null)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ThresholdSlider({
  id,
  threshold,
  onChange,
  setAuto,
}: {
  id: string;
  threshold: number | null;
  onChange: (threshold: number) => void;
  setAuto: () => void;
}) {
  const intl = useIntl();
  // Automatic has no position of its own, so the slider rests where the service's own default sits
  // rather than at an end — dragging from there is a nudge, not a jump from a number nobody chose.
  const index = threshold == null ? TICKS.indexOf(DEFAULT_TICK) : nearestTick(threshold);
  const value = tickAt(index);
  const kept = EXAMPLE_LABELS.filter((row) => matches(row.score, value)).length;

  return (
    <div className="px-2 pb-2 pt-1.5">
      <label className="block" htmlFor={`${id}-threshold`}>
        <span className="flex items-baseline justify-between text-smycles-label leading-none">
          <span className="opacity-60">
            {intl.formatMessage({ id: "agent.labelThreshold.label", defaultMessage: "Match threshold" })}
          </span>
          <span className="flex items-baseline gap-2">
            {/* Automatic as a state of this control rather than a menu row above it, which said
                "Match threshold" twice. The number still shows where the slider rests, dimmed,
                because that IS the service's default — it is just not a number the reader picked. */}
            <button
              type="button"
              aria-pressed={threshold == null}
              onClick={(event) => {
                event.preventDefault();
                setAuto();
              }}
              className="rounded px-1 py-0.5 text-smycles-micro leading-none"
              style={
                threshold == null
                  ? { color: "var(--color-ink-accent)", background: "color-mix(in srgb, var(--color-accent) 14%, transparent)" }
                  : { opacity: 0.5 }
              }
            >
              {intl.formatMessage({ id: "agent.labelThreshold.autoShort", defaultMessage: "auto" })}
            </button>
            <span className="font-medium tabular-nums" style={threshold == null ? { opacity: 0.4 } : undefined}>
              {value.toFixed(2)}
            </span>
          </span>
        </span>
        <input
          id={`${id}-threshold`}
          type="range"
          min={0}
          max={TICKS.length - 1}
          step={1}
          value={index}
          aria-valuetext={value.toFixed(2)}
          onChange={(event) => onChange(tickAt(Number(event.target.value)))}
          className="mt-1.5 w-full"
        />
      </label>

      <div className="mt-3 flex items-baseline justify-between text-smycles-micro leading-none opacity-60">
        <span className="truncate">
          {intl.formatMessage(
            { id: "agent.labelThreshold.example", defaultMessage: "Example: {word}" },
            { word: EXAMPLE_WORD },
          )}
        </span>
        <span className="shrink-0 tabular-nums">
          {kept}/{EXAMPLE_LABELS.length}
        </span>
      </div>

      {/* A LADDER, not a list of numbers. The bar is the similarity and the vertical rule is the
          threshold, so dragging the slider moves a cut line DOWN THROUGH the labels and the reader
          sees which ones fall below it — which is the one thing a number on its own cannot say. */}
      <div className="mt-1.5">
        {EXAMPLE_LABELS.map((row) => {
          const on = matches(row.score, value);
          return (
            <div
              key={row.label}
              data-matching={on}
              className="flex h-[15px] items-center gap-1.5 text-smycles-micro leading-none"
              style={{ opacity: on ? 1 : 0.4 }}
            >
              <span className="w-[132px] shrink-0 truncate" title={row.label}>
                {row.label}
              </span>
              <span className="bg-ink/[0.07] relative h-[7px] flex-1 rounded-[2px]">
                <span
                  className="absolute inset-y-0 left-0 rounded-[2px]"
                  style={{ width: `${bar(row.score)}%`, background: on ? "var(--color-accent)" : "var(--color-ink-ui)" }}
                />
                {/* Drawn per row and taller than the track, so the segments meet and read as one rule. */}
                <span
                  aria-hidden
                  className="absolute -bottom-1 -top-1 w-px"
                  style={{
                    left: `${bar(value)}%`,
                    background: "var(--color-accent)",
                    // The halo is what makes it visible ON a filled bar; without it the rule
                    // disappears into exactly the rows it is meant to be separating.
                    boxShadow: "0 0 0 1px var(--color-surface)",
                  }}
                />
              </span>
              <span className="w-6 shrink-0 text-right tabular-nums">{row.score.toFixed(2)}</span>
            </div>
          );
        })}
      </div>

      {/* Why the bottom of the slider does nothing, said rather than left to be discovered. */}
      {value <= VOCABULARY_FLOOR && (
        <p className="mt-2 text-smycles-micro leading-snug opacity-60">
          {intl.formatMessage(
            {
              id: "agent.labelThreshold.floor",
              defaultMessage: "Below {floor} every label matches: no two labels are less alike than that.",
            },
            { floor: VOCABULARY_FLOOR.toFixed(2) },
          )}
        </p>
      )}
    </div>
  );
}

/** The tick a stored threshold sits on — a value from an older session need not be one of ours. */
function nearestTick(threshold: number): number {
  let best = 0;
  TICKS.forEach((tick, i) => {
    if (Math.abs(tick - threshold) < Math.abs(tickAt(best) - threshold)) best = i;
  });
  return best;
}
